import React, { useEffect, useState } from 'react';
import ReactCalendar from 'react-calendar';
import CalendarForward from '../../assets/icon/CalendarForward';
import CalendarBack from '../../assets/icon/CalendarBack';
import { Detail, Value } from 'react-calendar/dist/cjs/shared/types';
import calenderApi from '../../api/calenderApi';
import useLocationStore from '../../store/locatonStore';
import {
  getAdjustedDate,
  getFirstAndLastDate,
  getFirstAndLastDateForCalender,
} from '../../utils/dateUtils';
import useBgColorStore from '../../store/bgColor';
import { toast } from 'react-toastify';
import { isNullOrEmpty } from '../../utils/commonUtils';
import { error } from 'console';
import useTimeStore from '../../store/timeStore';
import { BallTriangle } from 'react-loader-spinner';
import { showOrReplaceToast } from '../../utils/utility';
import { CUSTOM_TOAST_ID } from '../../constants/constant';
import useDateStore from '../../store/selectedDateStore';
import { useNavigate } from 'react-router-dom';

interface CalenderScreenProps {
  setComments?: (comments: string[]) => void;
  setSelectedDate?: (date: Date | null) => void;
}
interface BookingData {
  booked: number;
  totalBookings: number;
  bookingRate: number;
  mediaTravelingMessage:string,
  vipBookingMessage:string
}

const CalenderScreen: React.FC<CalenderScreenProps> = ({
  setComments,
  setSelectedDate,
}) => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState<Record<string, BookingData>>(
    {},
  );
  const { setSelectedDateFromStore } = useDateStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const { bgColor } = useBgColorStore();
  const [selectedDateInternal, setSelectedDateInternal] = useState<Date | null>(
    getAdjustedDate(),
  );
  const [loading, setLoading] = useState<boolean>(false);
  const operationTime = useTimeStore();

  useEffect(() => {
    handleDateChange(selectedDateInternal);
  }, []);

  const handleRedirectToOperation=(date:Date)=>{
    setSelectedDateFromStore(date)
    navigate('/operations');
  }

  const handleDateChange = async (value: Value) => {
    let date =
      value instanceof Date ? value : Array.isArray(value) ? value[0] : null;
    if (date) {
      setSelectedDate?.(date);
      setSelectedDateInternal(date);
      setSelectedDateFromStore(date)

      setLoading(true); // Start Loader here

      const firstDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        1,
      ).toLocaleDateString('en-CA');
      const lastDate = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0,
      ).toLocaleDateString('en-CA');

      try {
        await Promise.all([
          fetchCalendarDetails(date),
          fetchBookingDetails(firstDate, lastDate),
        ]);
      } catch (error) {
        console.error(error);
        showOrReplaceToast(
          CUSTOM_TOAST_ID.calender,
          'Something went wrong while loading',
          'error',
        );
      } finally {
        setLoading(false); // Stop Loader only after BOTH APIs
      }
    } else {
      setSelectedDate?.(null);
    }
  };

  const [detailsAreLoading, setDetailsAreLoading] = useState<boolean>(false);

  const fetchCalendarDetails = async (date: Date) => {
    if (!date) return;
    try {
      const ownerId = selectedLocation?.OwnerId;
      const formattedDate = date?.toLocaleDateString('en-CA');
      const response = await calenderApi.getCalendarDetails(
        ownerId,
        formattedDate,
      );
      const comments = response?.data?.general_comments;
      const commentsArray = comments?.split('\n');
      setComments?.(commentsArray);
    } catch (error) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        'Error fetching calendar details:',
        'error',
      );
    }
  };
  useEffect(() => {
    if (operationTime?.rateId) {
      const today = new Date();
      const { firstDate, lastDate } = getFirstAndLastDateForCalender(today);
      fetchBookingDetails(firstDate, lastDate);
    }
  }, [operationTime?.rateId]);

  const fetchBookingDetails = async (fromDate: string, toDate: string) => {
    if (!selectedDateInternal) return;
    try {
      setDetailsAreLoading(true);
      const ownerId = selectedLocation?.OwnerId;
      const rateId = operationTime?.rateId;
      const response = await calenderApi.getBookingDetailsOrderByDate(
        ownerId,
        rateId,
        fromDate,
        toDate,
      );
      setDetailsAreLoading(false);
      const data = response?.data;
      const mappedData: Record<
        string,
        { booked: number; totalBookings: number; bookingRate: number,mediaTravelingMessage:string,vipBookingMessage:string }
      > = {};

      if (isNullOrEmpty(data)) {
        throw new Error('No Data');
      }
      data?.forEach(
        (item: {
          booked: number;
          totalBookings: number;
          bookedDate: string;
          bookingRate: number;
          vipBookingMessage:string;
          mediaTravelingMessage:string;
        }) => {
          const dateKey = new Date(item?.bookedDate).toLocaleDateString(
            'en-CA',
          );
          mappedData[dateKey] = {
            booked: item?.booked,
            totalBookings: item?.totalBookings,
            bookingRate: item?.bookingRate,
            mediaTravelingMessage:item?.mediaTravelingMessage,
            vipBookingMessage:item?.vipBookingMessage
          };
        },
      );

      setBookingData(mappedData);
    } catch (error) {
      setDetailsAreLoading(false);
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        'Error fetching booking details:',
        'error',
      );
    }
  };

  const getTileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const dateKey = date.toLocaleDateString('en-CA');
      const booking = bookingData[dateKey];
      if (booking) {
        return (
          <div className="relative group  flex-col w-full items-center rounded-md mx-1 p-2 cursor-pointer flex ">
            <div className="flex justify-center">
              <span className="text-textColorGray">
                {booking?.booked}
              </span>
              <span className="text-textColorGray">/</span>
              <span className="text-calenderColor-topHeader">
                {booking?.totalBookings}
              </span>
            </div>
            <div
              className={`${
                booking?.bookingRate >= 0 ? 'text-green' : 'text-red-600'
              }`}
            >
              <span>
                {booking?.bookingRate > 0
                  ? `+${booking.bookingRate}`
                  : booking?.bookingRate}
              </span>
            </div>
            
            <div className="bg-black absolute top-1/2 z-10 hidden group-hover:block text-gray-800 text-sm rounded shadow-lg w-full bottom-full right-1/4 transform translate-x-1/2 mb-1">
            {booking?.mediaTravelingMessage && (
              <p className='text-gray-800 mb-2'>
                <span className="font-semibold">Media Traveling: </span>
                {booking.mediaTravelingMessage}
              </p>
            )}
            {booking?.vipBookingMessage && (
              <p className='text-gray-800'>
              
                {booking.vipBookingMessage}
              </p>
            )}
          </div>
          </div>
        );
      }
    }
    return null;
  };


  return (
    <div className="relative min-h-[400px] ">
      {loading ||
        (detailsAreLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 50,
            }}
          >
            <BallTriangle
              height="80"
              width="80"
              color="#4fa94d"
              ariaLabel="loading"
              visible={true}
            />
          </div>
        ))}
      
      <ReactCalendar
        value={selectedDateInternal}
        tileContent={getTileContent}
        className={`${bgColor}`}
        next2Label={null}
        prev2Label={null}
      tileClassName={({ date }) => {
      const dateKey = date.toLocaleDateString('en-CA');
      const booking = bookingData[dateKey];
      if (booking?.mediaTravelingMessage && booking?.vipBookingMessage) {
        return 'has-both-asterisks';
      } else if (booking?.mediaTravelingMessage) {
        return 'has-blue-asterisk';
      } else if (booking?.vipBookingMessage) {
        return 'has-red-asterisk';
      }
      return '';
      }}
        nextLabel={
          <div>
            <div className="block dark:hidden">
              <CalendarForward fillColor={'#FFFFFF'} pathColor={'#004E9B'} />
            </div>
            <div className="hidden dark:block">
              <CalendarForward fillColor={'#000000'} pathColor={'#FFFFFF'} />
            </div>
          </div>
        }
        prevLabel={
          <div>
            <div className="block dark:hidden">
              <CalendarBack fillColor={'#FFFFFF'} pathColor={'#004E9B'} />
            </div>
            <div className="hidden dark:block">
              <CalendarBack fillColor={'#000000'} pathColor={'#FFFFFF'} />
            </div>
          </div>
        }
          onClickDay={(value: Date) => {
          console.log('Selected Date:', value);
          handleRedirectToOperation(value); // optional: keep existing logic
          }}
        onActiveStartDateChange={async ({
          action,
          activeStartDate,
          view,
        }: {
          action: string | undefined;
          activeStartDate: Date | null;
          view: Detail;
        }) => {
          if (activeStartDate && view === 'month') {
            const firstDayOfMonth = new Date(activeStartDate.getFullYear(),activeStartDate.getMonth(),1);
            setSelectedDate?.(firstDayOfMonth);
            fetchCalendarDetails(firstDayOfMonth);
            setLoading(true);
            const FirstAndLastDate = getFirstAndLastDate(activeStartDate);
            const firstDate = FirstAndLastDate?.firstDate;
            const lastDate = FirstAndLastDate?.lastDate;
            try {
              await Promise.all([
                fetchCalendarDetails(firstDayOfMonth),
                fetchBookingDetails(firstDate, lastDate),
              ]);
            } catch (error) {
              console.error(error);
              showOrReplaceToast(
                CUSTOM_TOAST_ID.calender,
                'Something went wrong while loading',
                'error',
              );
            } finally {
              setLoading(false);
            }
          }
        }}
      />
    </div>
  );
};

export default CalenderScreen;
